/*
 * Copyright (c) 2020
 * Company: G-Portal
 * Filename: config.ts
 * Class: config.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version:
 */
import { RouteConfig } from 'vue-router';
import BaseRouterView from '../view/app/BaseRouterView.vue';
import RouteNames from './routes';
import BaseOrderCheckoutLayout from '../view/app/BaseOrderCheckoutLayout.vue';
import { useRouterMiddleware, lazyLoadView } from './routerFeatures';

// -- regexp for path matching
const productKeyPattern = '([A-Za-z0-9-_]*)';
const donateHashPattern = '([A-Za-z0-9]*)';
const uuidPattern = '([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})';

// -- route configuration
export const routes: RouteConfig[] = [
  {
    path: '/:regionPrefix(eur|int)',
    components: {
      content: BaseRouterView,
    },
    children: [
      {
        path: 'gamecloud',
        name: RouteNames.GamecloudDashboardRoute,
        components: { content: () => import(/* webpackChunkName: "vue.donation" */'../view/gamecloud/Dashboard.vue') },
        children:
          [
            { path: 'add/:cloudId(\\d+)', name: RouteNames.GamecloudDashboardAddRoute },
          ],
      },
      { path: 'referrer', name: RouteNames.ReferrerRoute },
      {
        path: 'profile',
        name: RouteNames.ProfileRoute,
        components: {
          content: BaseRouterView,
        },
        children: [
          {
            path: 'payments',
            components: { content: () => import(/* webpackChunkName: "vue.payments" */'../view/profile/Payments.vue') },
            meta: {
              requiresAuth: true,
            },
            children: [
              {
                path: '',
                redirect: '/:regionPrefix/profile/payments/transactions',
              },
              {
                path: 'transactions',
                name: RouteNames.ProfilePaymentTransactionRoute,
                components: {
                  // --- content is defined in the Payment.vue template
                  content: () => import(/* webpackChunkName: "vue.payments.transaction" */'../view/profile/payments/PaymentTransaction.vue'),
                },
              },
              {
                path: `recurring/:paymentId${uuidPattern}?`,
                name: RouteNames.ProfilePaymentRecurringRoute,
                components: {
                  // --- content is defined in the Payment.vue template
                  content: () => import(/* webpackChunkName: "vue.payments.recurring" */'../view/profile/payments/PaymentRecurring.vue'),
                },
              },
              {
                path: 'manage',
                name: RouteNames.ProfilePaymentManageRoute,
                components: {
                  // --- content is defined in the Payment.vue template
                  content: () => import(/* webpackChunkName: "vue.payments.manage" */'../view/profile/payments/PaymentManage.vue'),
                },
              },
              {
                path: 'invoices',
                name: RouteNames.ProfilePaymentInvoicesRoute,
                components: {
                  // --- content is defined in the Payment.vue template
                  content: () => import(/* webpackChunkName: "vue.payments.invoice" */'../view/profile/payments/invoices/PaymentInvoices.vue'),
                },
                meta: {
                  requiresAdmin: window.regionPrefix == 'int',
                },
              },
            ],
          },
        ],
      },
      {
        path: `server/:productKey${productKeyPattern}+`,
        name: RouteNames.ServerRoute,
        components: {
          content: BaseRouterView,
        },
        children: [
          // -- info: this is required for the menu to open the correct view
          {
            path: ':serverId(\\d+)',
            name: RouteNames.ServerStatusRoute,
            components: {
              content: () => import(/* webpackChunkName: "vue.server.status" */'../view/server/BaseServerStatusPage.vue'),
            },
            beforeEnter: async (to, from, next) => {
              await useRouterMiddleware(to).requestExtensionNamespace(next);
            },
          },
          { path: ':serverId(\\d+)/:action?', name: RouteNames.ServerSettingsRoute },
          { path: ':serverId(\\d+)/system/:action', name: RouteNames.ServerAdministrationRoute },
          { path: 'mods/mods/:serverId(\\d+)', name: RouteNames.ServerContentRoute },
        ],
      },
      {
        path: `donate/:donationHash${donateHashPattern}/:userName`,
        meta: {
          requiresAuth: true,
        },
        name: RouteNames.DonationRoute,
        components: {
          content: () => import(/* webpackChunkName: "vue.donation" */'../view/donation/Donation.vue'),
        },
      },
      {
        path: 'order',
        components: {
          content: BaseOrderCheckoutLayout,
        },
        children: [
          {
            path: `step/three/:productKey/:paymentId${uuidPattern}?`,
            name: RouteNames.StepThreeRoute,
            meta: {
              requiresAuth: true,
            },
            beforeEnter: async (to, from, next) => {
              await useRouterMiddleware(to).requestExtensionNamespace(next);
            },
            component: () => import(/* webpackChunkName: "vue.stepthree" */'../view/checkout/StepThree.vue'),
          },
          {
            path: 'step/four/:productKey',
            name: RouteNames.StepFourRoute,
            meta: {
              // @TODO: add translated key
              // title: i18n.t('stepfour:meta.title'),
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "vue.stepfour" */'../view/checkout/StepFour.vue'),
          },
          {
            path: 'pending',
            name: RouteNames.PaymentPendingRoute,
            meta: {
              requiresAuth: true,
            },
            component: () => import(/* webpackChunkName: "vue.pending" */'../view/payment/BasePendingView.vue'),
          },
        ],
      },
    ],
  },
  {
    // path: `/:localePrefix(${configurationPluginManager.data.routes.patterns.localePrefixes})`,
    path: '/:languagePrefix(us|ch|ca|ar|mx|au|nz|ja|en|pt|gb|nl|es|pl|fr|it|ru|at|de|br|zh)/:territoryPrefix(en|it|us|es|ch|fr|ca|de)?',

    components: {
      content: BaseRouterView,
    },
    children: [
      {
        path: '',
        name: RouteNames.HomepageRoute,
        components: {
          content: () => import(/* webpackChunkName: "vue.homepage" */'../view/homepage/Homepage.vue'),
        },
      },
      { path: 'gameserver/index', name: RouteNames.GameserverProductsRoute },
      { path: 'esports-gameserver-eps/index', name: RouteNames.EpsGameserverOverview },
      {
        path: 'news',
        name: RouteNames.NewsOverviewRoute,
        components: {
          content: () => lazyLoadView(
            import(/* webpackChunkName: "vue.news" */'../view/news/News.vue'),
          ),
        },
      },
      {
        path: ':landingpagePrefix(gameserver|serveur-de-jeu|server-di-gioco|serwery-gier|servidor-de-jogos|creator-bundles|voiceserver|server-di-voce)/:slug',
        name: RouteNames.LandingpageRoute,
        components: {
          content: () => import(/* webpackChunkName: "vue.landingpage" */'../view/landingpage/BaseLandingPage.vue'),
        },
      },
      {
        path: ':newsPrefix(news)/:newsSlug',
        name: RouteNames.NewsDetailRoute,
        components: {
          content: () => lazyLoadView(
            import(/* webpackChunkName: "vue.newsdetail" */'../view/news/NewsDetail.vue'),
          ),
        },
      },
      {
        path: ':textPrefix(term-and-conditions|testslugd|testsluge|testslugf)(/:textSlug)?',
        name: RouteNames.TextRoute,
        components: {
          content: () => import(/* webpackChunkName: "vue.textpage" */'../view/textpage/TextPage.vue'),
        },
      },
      {
        path: 'order',
        components: {
          content: BaseOrderCheckoutLayout,
        },
        children: [
          {
            path: 'step/one/:productKey',
            name: RouteNames.StepOneRoute,
            props: {
              priceTimeout: 500,
            },
            beforeEnter: async (to, from, next) => {
              await useRouterMiddleware(to).requestExtensionNamespace(next);
            },
            component: () => import(/* webpackChunkName: "vue.stepone" */'../view/checkout/TheStepOneView.vue'),
          },
        ],
      },
      {
        path: 'support',
        name: RouteNames.SupportRoute,
      },
    ],
  },
  { path: '*', name: RouteNames.NotFound },
];
